import { emptySplitApi as api } from "./baseApi";
import type {
  JobDescription,
  OrgChartNode,
  ReportsCounts,
} from "../components/reviews/bscTypes";

// Define User type for org chart
interface OrgChartUser {
  id: string;
  name?: string;
  email: string;
  jobTitle?: string;
  department?: string;
  reportsToUserId?: string;
  jobDescriptionId?: string;
  grade?: string;
}

export const addTagTypes = [
  "JobDescriptionController",
  "OrgChartController",
] as const;

const jobDescriptionApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    overrideExisting: true,
    endpoints: (build) => ({
      // ===== JOB DESCRIPTION ENDPOINTS =====

      getJobDescriptions: build.query<JobDescription[], { filter?: any }>({
        query: (queryArg) => ({
          url: `/job-descriptions`,
          params: queryArg.filter
            ? { filter: JSON.stringify(queryArg.filter) }
            : undefined,
        }),
        providesTags: ["JobDescriptionController"],
      }),

      getJobDescriptionsActive: build.query<JobDescription[], void>({
        query: () => ({ url: `/job-descriptions/active` }),
        providesTags: ["JobDescriptionController"],
      }),

      getMyJobDescription: build.query<JobDescription | null, void>({
        query: () => ({ url: `/job-descriptions/my` }),
        providesTags: ["JobDescriptionController"],
      }),

      getTeamJobDescriptions: build.query<JobDescription[], void>({
        query: () => ({ url: `/job-descriptions/team` }),
        providesTags: ["JobDescriptionController"],
      }),

      getJobDescriptionsCount: build.query<{ count: number }, { where?: any }>({
        query: (queryArg) => ({
          url: `/job-descriptions/count`,
          params: queryArg.where
            ? { where: JSON.stringify(queryArg.where) }
            : undefined,
        }),
        providesTags: ["JobDescriptionController"],
      }),

      getJobDescriptionById: build.query<JobDescription, { id: string }>({
        query: (queryArg) => ({ url: `/job-descriptions/${queryArg.id}` }),
        providesTags: ["JobDescriptionController"],
      }),

      getJobDescriptionByUserId: build.query<
        JobDescription,
        { userId: string }
      >({
        query: (queryArg) => ({
          url: `/job-descriptions/user/${queryArg.userId}`,
        }),
        providesTags: ["JobDescriptionController"],
      }),

      getJobDescriptionsByDepartment: build.query<
        JobDescription[],
        { department: string }
      >({
        query: (queryArg) => ({
          url: `/job-descriptions/by-department/${queryArg.department}`,
        }),
        providesTags: ["JobDescriptionController"],
      }),

      getJobDescriptionDepartments: build.query<string[], void>({
        query: () => ({ url: `/job-descriptions/departments` }),
        providesTags: ["JobDescriptionController"],
      }),

      getJobDescriptionGrades: build.query<string[], void>({
        query: () => ({ url: `/job-descriptions/grades` }),
        providesTags: ["JobDescriptionController"],
      }),

      getJobDescriptionReportsCounts: build.query<
        ReportsCounts,
        { id: string }
      >({
        query: (queryArg) => ({
          url: `/job-descriptions/${queryArg.id}/reports-counts`,
        }),
        providesTags: ["JobDescriptionController", "OrgChartController"],
      }),

      createJobDescription: build.mutation<
        JobDescription,
        Omit<JobDescription, "id" | "createdAt" | "updatedAt">
      >({
        query: (body) => ({
          url: `/job-descriptions`,
          method: "POST",
          body,
        }),
        invalidatesTags: ["JobDescriptionController"],
      }),

      // Self-management endpoints (for users to manage their own JD)
      createMyJobDescription: build.mutation<
        JobDescription,
        Omit<
          JobDescription,
          | "id"
          | "createdAt"
          | "updatedAt"
          | "assignedUserId"
          | "assignedUserName"
          | "createdByUserId"
          | "createdByName"
        >
      >({
        query: (body) => ({
          url: `/job-descriptions/my`,
          method: "POST",
          body,
        }),
        invalidatesTags: ["JobDescriptionController"],
      }),

      updateMyJobDescription: build.mutation<
        JobDescription,
        Partial<JobDescription>
      >({
        query: (body) => ({
          url: `/job-descriptions/my`,
          method: "PATCH",
          body,
        }),
        invalidatesTags: ["JobDescriptionController"],
      }),

      deleteMyJobDescription: build.mutation<void, void>({
        query: () => ({
          url: `/job-descriptions/my`,
          method: "DELETE",
        }),
        invalidatesTags: ["JobDescriptionController"],
      }),

      updateJobDescription: build.mutation<
        void,
        { id: string; body: Partial<JobDescription> }
      >({
        query: (queryArg) => ({
          url: `/job-descriptions/${queryArg.id}`,
          method: "PATCH",
          body: queryArg.body,
        }),
        invalidatesTags: ["JobDescriptionController"],
      }),

      deleteJobDescription: build.mutation<void, { id: string }>({
        query: (queryArg) => ({
          url: `/job-descriptions/${queryArg.id}`,
          method: "DELETE",
        }),
        invalidatesTags: ["JobDescriptionController"],
      }),

      assignUserToJobDescription: build.mutation<
        JobDescription,
        { id: string; userId: string }
      >({
        query: (queryArg) => ({
          url: `/job-descriptions/${queryArg.id}/assign`,
          method: "POST",
          body: { userId: queryArg.userId },
        }),
        invalidatesTags: ["JobDescriptionController"],
      }),

      unassignUserFromJobDescription: build.mutation<void, { id: string }>({
        query: (queryArg) => ({
          url: `/job-descriptions/${queryArg.id}/unassign`,
          method: "POST",
        }),
        invalidatesTags: ["JobDescriptionController"],
      }),

      // ===== ORG CHART ENDPOINTS =====

      getOrgChart: build.query<OrgChartNode[], void>({
        query: () => ({ url: `/org-chart` }),
        providesTags: ["OrgChartController"],
      }),

      getUserOrgChart: build.query<OrgChartNode, { userId: string }>({
        query: (queryArg) => ({
          url: `/org-chart/user/${queryArg.userId}`,
        }),
        providesTags: ["OrgChartController"],
      }),

      getUserReportingChain: build.query<OrgChartUser[], { userId: string }>({
        query: (queryArg) => ({
          url: `/org-chart/user/${queryArg.userId}/reporting-chain`,
        }),
        providesTags: ["OrgChartController"],
      }),

      getUserDirectReports: build.query<OrgChartUser[], { userId: string }>({
        query: (queryArg) => ({
          url: `/org-chart/user/${queryArg.userId}/direct-reports`,
        }),
        providesTags: ["OrgChartController"],
      }),

      getPotentialSupervisors: build.query<
        OrgChartUser[],
        { excludeUserId?: string }
      >({
        query: (queryArg) => ({
          url: `/org-chart/supervisors`,
          params: { excludeUserId: queryArg.excludeUserId },
        }),
        providesTags: ["OrgChartController"],
      }),

      getDepartmentStructure: build.query<
        OrgChartUser[],
        { department: string }
      >({
        query: (queryArg) => ({
          url: `/org-chart/department/${queryArg.department}`,
        }),
        providesTags: ["OrgChartController"],
      }),
    }),
  });

export { jobDescriptionApi };

export const {
  // Job Description hooks
  useGetJobDescriptionsQuery,
  useLazyGetJobDescriptionsQuery,
  useGetJobDescriptionsActiveQuery,
  useLazyGetJobDescriptionsActiveQuery,
  useGetMyJobDescriptionQuery,
  useLazyGetMyJobDescriptionQuery,
  useGetTeamJobDescriptionsQuery,
  useLazyGetTeamJobDescriptionsQuery,
  useGetJobDescriptionsCountQuery,
  useGetJobDescriptionByIdQuery,
  useLazyGetJobDescriptionByIdQuery,
  useGetJobDescriptionByUserIdQuery,
  useLazyGetJobDescriptionByUserIdQuery,
  useGetJobDescriptionsByDepartmentQuery,
  useGetJobDescriptionDepartmentsQuery,
  useGetJobDescriptionGradesQuery,
  useGetJobDescriptionReportsCountsQuery,
  useCreateJobDescriptionMutation,
  useCreateMyJobDescriptionMutation,
  useUpdateMyJobDescriptionMutation,
  useDeleteMyJobDescriptionMutation,
  useUpdateJobDescriptionMutation,
  useDeleteJobDescriptionMutation,
  useAssignUserToJobDescriptionMutation,
  useUnassignUserFromJobDescriptionMutation,

  // Org Chart hooks
  useGetOrgChartQuery,
  useLazyGetOrgChartQuery,
  useGetUserOrgChartQuery,
  useGetUserReportingChainQuery,
  useGetUserDirectReportsQuery,
  useGetPotentialSupervisorsQuery,
  useLazyGetPotentialSupervisorsQuery,
  useGetDepartmentStructureQuery,
} = jobDescriptionApi;
